import React, { createContext, useContext, useState, useEffect } from 'react';
import zhTC from '../locales/zh-TC';
import zhSC from '../locales/zh-SC';
import en from '../locales/en';
import onboardingZhTC from '../locales/onboarding-zh-TC';
import onboardingZhSC from '../locales/onboarding-zh-SC';
import onboardingEn from '../locales/onboarding-en';

const locales = {
  'zh-TC': { ...zhTC, ...onboardingZhTC },
  'zh-SC': { ...zhSC, ...onboardingZhSC },
  'en': { ...en, ...onboardingEn }
};

/** 佔位符 {name}；0、false 等合法值需顯示，不可用 || 否則會留下 {count} */
function applyTranslationParams(templateStr, params) {
  if (typeof templateStr !== 'string') return templateStr;
  return templateStr.replace(/\{(\w+)\}/g, (match, paramName) => {
    if (!Object.prototype.hasOwnProperty.call(params, paramName)) return match;
    const v = params[paramName];
    if (v === undefined || v === null) return match;
    return String(v);
  });
}

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('zh-TC');
  const [translations, setTranslations] = useState(locales['zh-TC']);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // 從 localStorage 讀取語言設定
    const savedLanguage = localStorage.getItem('language') || 'zh-TC';
    console.debug('LanguageProvider: 初始化語言為', savedLanguage);
    
    // 確保語言文件存在
    if (locales[savedLanguage]) {
      setCurrentLanguage(savedLanguage);
      setTranslations(locales[savedLanguage]);
    } else {
      console.warn(`Language ${savedLanguage} not found, falling back to zh-TC`);
      setCurrentLanguage('zh-TC');
      setTranslations(locales['zh-TC']);
    }
    
    setIsReady(true);
  }, []);

  const changeLanguage = (language) => {
    console.debug('LanguageProvider: 切換語言到', language);
    
    if (locales[language]) {
      setCurrentLanguage(language);
      setTranslations(locales[language]);
      localStorage.setItem('language', language);
    } else {
      console.warn(`Language ${language} not found, keeping current language`);
    }
  };

  const t = (key, params = {}) => {
    // 如果翻譯系統還沒準備好，返回鍵值
    if (!isReady) {
      return key;
    }

    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // 只在開發模式下輸出警告，避免生產環境中的重複警告
        if (process.env.NODE_ENV === 'development') {
          console.warn(`Translation key not found: ${key} at step "${k}"`);
        }
        // 嘗試從其他語言文件中獲取翻譯作為回退
        for (const lang of Object.keys(locales)) {
          if (lang !== currentLanguage) {
            let fallbackValue = locales[lang];
            for (const fallbackKey of keys) {
              if (fallbackValue && typeof fallbackValue === 'object' && fallbackKey in fallbackValue) {
                fallbackValue = fallbackValue[fallbackKey];
              } else {
                fallbackValue = null;
                break;
              }
            }
            if (fallbackValue && typeof fallbackValue === 'string') {
              console.debug(`Using fallback translation from ${lang} for key: ${key}`);
              return applyTranslationParams(fallbackValue, params);
            }
          }
        }
        return key;
      }
    }

    if (typeof value === 'string') {
      return applyTranslationParams(value, params);
    }

    return value;
  };

  /** AI 員工下拉：canonical value（與 DB／ai-employee-lookups.json 一致）→ 目前語系顯示文字 */
  const tLookup = React.useCallback(
    (category, canonicalValue) => {
      if (!isReady || canonicalValue == null || canonicalValue === '') {
        return canonicalValue ?? '';
      }
      const map = translations?.aiEmployeeProfiles?.lookupLabels?.[category];
      if (map && typeof map === 'object' && Object.prototype.hasOwnProperty.call(map, canonicalValue)) {
        const s = map[canonicalValue];
        if (typeof s === 'string' && s.length > 0) return s;
      }
      return String(canonicalValue);
    },
    [translations, isReady]
  );

  // 調試信息（使用 debug 級別，避免主控台噪音）
  useEffect(() => {
    if (isReady) {
      console.debug('LanguageProvider: 當前語言', currentLanguage);
      console.debug('LanguageProvider: 可用翻譯鍵', Object.keys(translations));
    }
  }, [currentLanguage, translations, isReady]);

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    tLookup,
    availableLanguages: Object.keys(locales),
    isReady
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}; 